import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, LOGO } from "../lib/data";

const LINKS = [
  { label: "About Us", href: "#about" },
  { label: "Case Studies", href: "#portfolio" },
  { label: "Clients", href: "#clients" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const prev = scrollY.getPrevious() ?? 0;
    setScrolled(latest > 40);
    if (latest > prev && latest > 300 && !megaOpen) setHidden(true);
    else setHidden(false);
  });

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -100 }}
        animate={{ y: hidden ? -120 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#0c0c11]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex items-center justify-between px-6 md:px-12 lg:px-16 h-[72px]">
          <a href="#top" data-testid="logo-link" className="flex items-center">
            <img src={LOGO} alt="Future Techmates" className="h-8 md:h-10 w-auto" />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                data-testid="nav-what-we-do"
                className="flex items-center gap-1.5 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                What We Do
                <ChevronDown size={15} className={`transition-transform ${megaOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[var(--accent-secondary)] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-consultation"
              data-magnetic
              className="rounded-full bg-[var(--accent-secondary)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--accent-secondary-hover)] transition-colors"
            >
              Free Consultation
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>

        {/* Mega menu */}
        <AnimatePresence>
          {megaOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
              data-testid="mega-menu"
              className="hidden lg:block absolute left-0 right-0 top-[72px] bg-[#0c0c11]/95 backdrop-blur-xl border-t border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
            >
              <div className="mx-auto px-12 lg:px-16 py-8 grid grid-cols-3 gap-x-10 gap-y-4">
                {SERVICES.map((s) => (
                  <a
                    key={s.n}
                    href="#what-we-do"
                    className="group flex items-start gap-3 py-2 rounded-lg hover:bg-white/10 px-3 -mx-3 transition-colors"
                    onClick={() => setMegaOpen(false)}
                  >
                    <span className="font-display text-xs text-[var(--accent-primary-hover)] mt-1">{s.n}</span>
                    <div>
                      <div className="text-sm font-semibold text-white group-hover:text-[var(--accent-secondary)] transition-colors">
                        {s.title}
                      </div>
                      <div className="text-xs text-zinc-400">{s.tag}</div>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[var(--bg-primary)] lg:hidden pt-24 px-6 overflow-y-auto"
            data-testid="mobile-drawer"
          >
            <div className="flex flex-col gap-1">
              <a href="#what-we-do" onClick={() => setMobileOpen(false)} className="py-4 text-2xl font-display font-semibold border-b border-white/10">What We Do</a>
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-4 text-2xl font-display font-semibold border-b border-white/10"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setMobileOpen(false)}
                className="mt-6 rounded-full bg-[var(--accent-secondary)] px-6 py-4 text-center text-base font-semibold text-white"
              >
                Free Consultation
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
